import React from 'react';
import styles from './defaultSearchKeywordsComponent.module.scss';
import i18n from 'i18n';
import { camelCase, defaultTo } from 'lodash';
import classnames from 'classnames/bind';
import { getNumberRangeIndexHint } from 'utils/StringUtil';
import { KeywordSource, SuggestKeywords } from 'core/product/Product';
import { resolveKeywordBidRange } from 'core/pchome/KeywordBidCompetitiveness';

const cx = classnames.bind(styles);

const nameHeaderFormatter = (column: any, _1: any, { sortElement }: any) => (
  <div className={styles.nameHeader}>
    {i18n.t<string>(column.text)}
    {sortElement}
  </div>
);

const nameFormatter = (suggestKeyword: any): any => (
  <div className={styles.nameCell}>
    <div className={styles.info}>
      <div className={styles.name}>
        {suggestKeyword}
      </div>
    </div>
  </div>
);

const keywordSearchVolumeFormatter = (keywordSearchVolume: any) => {
  const request = defaultTo(keywordSearchVolume, 0);
  const value = getNumberRangeIndexHint(request, 500, 10000);
  return (
    <div className={styles.keywordSearchVolumeCell}>
      {value}
    </div>
  );
};

const growthRateFormatter = (growthRate: any) => {
  const numeric = typeof growthRate === 'number' ? growthRate : 0;
  const percent = (numeric * 100).toFixed(2);
  const value = !growthRate ? '-' : numeric > 0 ? `+${percent}%` : `${percent}%`;
  return (
    <div
      className={cx(styles.growthRateCell, {
        [styles.growthRatePositive]: numeric > 0,
        [styles.growthRateNegative]: numeric < 0
      })}
    >
      {value}
    </div>
  );
};

const keywordAdCompetitionFormatter = (keywordAdCompetition: any) => {
  const fillRate = Math.max(1, defaultTo(keywordAdCompetition, 0));
  return (
    <div className={styles.keywordAdCompetitionCell}>
      {Array.from({ length: fillRate }).map((_, index) => (<div key={index} className={styles.fillRateBlock} />))}
    </div>
  );
};

/**
 * 建議出價區間。四個顯示點共用這一份（`優化目標` 的已選清單 / 關鍵字推薦 / 出價試算面板，
 * 加上 `基本資訊` 的出價效益參考卡片）⇒ 區間怎麼來的只寫在這裡一次，各補一次必漂。
 *
 * 顯示的區間與 `getKeywordBidCompetitiveness` 判定用的必須是同一個 ——
 * 兩者都走 `resolveKeywordBidRange`，所以「畫面上寫 8–20、狀態卻照別的數字算」不可能發生。
 * 沒有 CPC 資料的字會拿到預設區間 8–20（不是 wire 上那組 `[20, 20]` fallback，
 * 也不是 `0–0`）—— 緣由見 `core/pchome/KeywordBidCompetitiveness`。
 *
 * 這一欄不是 dummy field，底層 `Cell` 只在 `bidPrice` 變動時重畫（見 `rmn-web-stickable-table`）。
 * 佔位列（`bidPrice` 為 0）被真實資料回填時 `bidPrice` 一定會變 ⇒ 夠用。
 * 但注意反過來：顯示的 8–20 與底層的 0 或 20 不同，所以別用畫面上的數字去推 row 的內容。
 *
 * `bidFloor`（經銷商出價下限）不在 row 裡,它隨「優化目標」變而 row 不變 ⇒ 光靠
 * `bidPrice` 那條重畫條件接不住它。呼叫端必須同時把 `bidFloor` 放進 column 的
 * `formatExtraData` —— Cell 認 `row` 與 `formatExtraData` 的變動,那才是這一欄跟著
 * 優化目標更新的唯一機制。少了它,切 CPC↔CPM 後區間會停在舊值而且不報錯。
 */
const bidRangeFormatter = (
  _bidPrice: number,
  row: Pick<SuggestKeywords, 'bidPrice' | 'maxCpc' | 'cpcCounts'>,
  bidFloor: number
) => {
  const currency = i18n.t<string>('suggestKeywordsList.labels.postfix.price');
  const { floor, cap } = resolveKeywordBidRange(row, bidFloor);
  return (
    <div className={styles.bidPriceCell}>
      {`${floor}–${cap} ${currency}`}
    </div>
  );
};

const CLASS_BY_SOURCE: Record<string, string> = {
  [KeywordSource.MANUAL]: styles.sourceTagManual,
  [KeywordSource.SYSTEM]: styles.sourceTagSystem,
  [KeywordSource.TRENDING]: styles.sourceTagTrending,
  [KeywordSource.PROPOSAL]: styles.sourceTagProposal
};

const sourceFormatter = (sources: KeywordSource[] | undefined) => {
  if (!sources || sources.length === 0) {
    return <div className={styles.sourceTagList} />;
  }
  return (
    <div className={styles.sourceTagList}>
      {sources.map(src => {
        const labelKey = `keywordSource.labels.${camelCase(src)}`;
        const label = i18n.exists(labelKey) ? i18n.t<string>(labelKey) : src;
        return (
          <span key={src} className={cx(styles.sourceTag, CLASS_BY_SOURCE[src])}>
            {label}
          </span>
        );
      })}
    </div>
  );
};

const formatters = {
  nameHeaderFormatter,
  nameFormatter,
  bidRangeFormatter,
  keywordSearchVolumeFormatter,
  growthRateFormatter,
  keywordAdCompetitionFormatter,
  sourceFormatter
};
export default formatters;
